import { useState, useEffect } from 'react';
import { useOidcContext } from '../contexts/OidcContext';
import { Navigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { keyframes } from '@mui/material/styles';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { useBranding } from '../contexts/BrandingContext';
import { XxllncAiLogo } from '../components/icons';
import { useTranslation } from '../i18n/useTranslation';
import { api } from '../api';
import SignupProgress from '../components/SignupProgress';
import {
  saveSignupIntent,
  readSignupIntent,
  type SignupIntent,
} from '../utils/signupIntent';

const logoFadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const cardSlideUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const stepFadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

type LoginStep = 'email' | 'loading' | 'register' | 'error';

const DIRECT_BROKER_INTENT_KEY = 'portal_direct_broker_intent';

const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

function GoogleGlyph() {
  return (
    <svg width={18} height={18} viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4818h4.8436c-.2086 1.125-.8427 2.0782-1.7964 2.7164v2.2581h2.9087c1.7018-1.5668 2.6841-3.874 2.6841-6.6154z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.4673-.8059 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.8591-3.0477.8591-2.344 0-4.3282-1.5831-5.0359-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z"/>
      <path fill="#FBBC05" d="M3.9641 10.71c-.18-.54-.2823-1.1168-.2823-1.71s.1023-1.17.2823-1.71V4.9582H.9573A8.9969 8.9969 0 0 0 0 9c0 1.4523.3477 2.8268.9573 4.0418L3.9641 10.71z"/>
      <path fill="#EA4335" d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.3459l2.5813-2.5814C13.4632.8918 11.4259 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.9641 7.29C4.6718 5.1627 6.656 3.5795 9 3.5795z"/>
    </svg>
  );
}

function EmailGlyph() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="none"
        stroke="rgba(0,0,0,0.62)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 6h18v12H3z M3 7l9 7 9-7"
      />
    </svg>
  );
}

function MicrosoftGlyph() {
  return (
    <svg width={18} height={18} viewBox="0 0 18 18" aria-hidden="true">
      <rect x="0" y="0" width="8.5" height="8.5" fill="#F25022"/>
      <rect x="9.5" y="0" width="8.5" height="8.5" fill="#7FBA00"/>
      <rect x="0" y="9.5" width="8.5" height="8.5" fill="#00A4EF"/>
      <rect x="9.5" y="9.5" width="8.5" height="8.5" fill="#FFB900"/>
    </svg>
  );
}

interface BrandButtonProps {
  glyph: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

function BrandButton({ glyph, label, onClick, disabled }: BrandButtonProps) {
  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      sx={{
        'display': 'flex',
        'alignItems': 'center',
        'justifyContent': 'center',
        'gap': 1.25,
        'width': '100%',
        'height': 46,
        'px': 2,
        'borderRadius': '999px',
        'border': '1px solid',
        'borderColor': 'primary.main',
        'bgcolor': '#fff',
        'color': 'primary.main',
        'fontFamily': "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        'fontSize': '0.9rem',
        'fontWeight': 600,
        'letterSpacing': '0.01em',
        'transition': 'box-shadow 150ms ease, border-color 150ms ease, background-color 150ms ease, transform 150ms ease',
        '&:hover': {
          bgcolor: 'rgba(75, 46, 131, 0.04)',
          boxShadow: '0 1px 3px rgba(75,46,131,0.08), 0 4px 12px rgba(75,46,131,0.06)',
        },
        '&:active': { transform: 'translateY(0.5px)' },
        '&.Mui-disabled': { opacity: 0.45, cursor: 'not-allowed' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>{glyph}</Box>
      <Box component="span" sx={{ flex: '0 1 auto' }}>{label}</Box>
    </ButtonBase>
  );
}

export default function LoginPage() {
  const { isAuthenticated, loginWithRedirect } = useOidcContext();
  const branding = useBranding();
  const t = useTranslation();
  const [email, setEmail] = useState('');
  const [orgName, setOrgName] = useState('');
  const [step, setStep] = useState<LoginStep>('email');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  // Snapshot URL params once on mount; do NOT strip from history — React 18 StrictMode remount would land on the empty email form otherwise.
  const [autoEmail] = useState<string | null>(() => {
    const auto = new URLSearchParams(window.location.search).get('auto');
    return auto && isValidEmail(auto) ? auto : null;
  });
  // Broker alias for post-signup auto-login. URL param is the only reliable carrier across the KC logout redirect (sessionStorage is already cleared by AuthGuard).
  const [autoIdp] = useState<'google' | 'microsoft-entra' | null>(() => {
    const idp = new URLSearchParams(window.location.search).get('idp');
    return idp === 'google' || idp === 'microsoft-entra' ? idp : null;
  });
  const [sessionExpired] = useState<boolean>(
    () => new URLSearchParams(window.location.search).get('expired') === '1',
  );

  const handleSignIn = (addr: string, idpHint: string | null) => {
    void loginWithRedirect({
      authorizationParams: {
        login_hint: addr,
        scope: 'openid profile email organization',
        // Force fresh credentials so a dashboard realm cookie can't silently sign in as the admin.
        prompt: 'login',
        ...(idpHint ? { kc_idp_hint: idpHint } : {}),
      },
    });
  };

  // Brand-new user whose email domain matches an existing org: KC's login form has no
  // register link, so send them straight to native registration (no org-signup intent).
  // After they verify their email, AuthGuard's authenticated join flow
  // (getJoinableOrg → JoinPromptCard) offers "Request access" to the matching org.
  const handleRegisterToJoin = (addr: string) => {
    void loginWithRedirect({
      authorizationParams: {
        login_hint: addr,
        scope: 'openid profile email',
        prompt: 'create',
      },
    });
  };

  // Direct broker login (no email probe). prompt=login drops stale auth-session state so first-broker-login can attach the new identity without `different_user_authenticated`.
  const handleDirectBrokerLogin = (idpAlias: 'google' | 'microsoft-entra') => {
    // Flag so AuthGuard can offer fresh-signup recovery (instead of dead-ending on AccessDeniedCard) when the authenticated user has no org.
    sessionStorage.setItem(DIRECT_BROKER_INTENT_KEY, idpAlias);
    void loginWithRedirect({
      authorizationParams: {
        kc_idp_hint: idpAlias,
        scope: 'openid profile email organization',
        prompt: 'login',
      },
    });
  };

  const probeLoginFor = async (addr: string) => {
    setStep('loading');
    try {
      const data = await api.getLoginInfo(addr);
      if (!data.known) {
        // No org for this domain → register a brand-new organization.
        setStep('register');
      } else if (data.idp_hint) {
        // Broker org (member or new): the broker handles login + first-broker-login auto-join.
        handleSignIn(addr, data.idp_hint);
      } else if (data.account_exists) {
        // Existing account on a password/email org → sign in (post-auth join if not a member).
        handleSignIn(addr, null);
      } else {
        // Domain matches an org but this email has no account yet → register, then join post-auth.
        handleRegisterToJoin(addr);
      }
    } catch (err) {
      const status = (err as Error & { status?: number }).status;
      if (status === 503) {
        setErrorDetail(t('login.idpUnavailable'));
      } else {
        setErrorDetail(t('login.serverUnreachable'));
      }
      setStep('error');
    }
  };

  const probeLogin = () => probeLoginFor(email);

  const handleSignupWithBroker = (idpAlias: 'google' | 'microsoft-entra') => {
    if (!orgName.trim()) return;
    const intent: SignupIntent = { email, organizationName: orgName.trim(), idpAlias };
    saveSignupIntent(intent);
    void loginWithRedirect({
      authorizationParams: {
        login_hint: email,
        kc_idp_hint: idpAlias,
        scope: 'openid profile email',
        // Same as handleSignIn: prevent the dashboard's realm cookie from hijacking this signup with the admin's identity.
        prompt: 'login',
      },
    });
  };

  // Email-fallback signup uses KC's native registration form. After verification, AuthGuard calls /org_signup. `prompt=create` jumps straight to the registration form.
  const handleSignupWithEmail = () => {
    if (!orgName.trim()) return;
    const intent: SignupIntent = {
      email,
      organizationName: orgName.trim(),
      idpAlias: 'email',
    };
    saveSignupIntent(intent);
    void loginWithRedirect({
      authorizationParams: {
        login_hint: email,
        scope: 'openid profile email',
        prompt: 'create',
      },
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && isValidEmail(email)) {
      void probeLogin();
    }
  };

  const handleBack = () => {
    setStep('email');
    setOrgName('');
    setErrorDetail(null);
  };

  useEffect(() => {
    if (!autoEmail) return;
    // Brief delay so the "Signing you in" transition card is legible before the broker redirect.
    const timer = window.setTimeout(() => {
      setEmail(autoEmail);
      // Post-signup: URL `?idp=...` wins (AuthGuard clears storage before logout). Storage is a defensive fallback.
      let signupHint: string | null = autoIdp;
      if (!signupHint) {
        signupHint = readSignupIntent(autoEmail)?.idpAlias ?? null;
      }
      // 'email' is not a KC broker alias — strip it to avoid a `kc_idp_hint=email` lookup failure.
      const brokerHint = signupHint === 'google' || signupHint === 'microsoft-entra'
        ? signupHint
        : null;
      handleSignIn(autoEmail, brokerHint);
    }, 1100);
    return () => window.clearTimeout(timer);
  }, [autoEmail, autoIdp]);

  if (isAuthenticated) {
    // Cover the niche case where a user lands here while already authenticated:
    // restore the URL stashed by AuthGuard (same-origin path only).
    const stash = sessionStorage.getItem('portal_return_to');
    const safe = stash && stash.startsWith('/') && !stash.startsWith('//') ? stash : null;
    if (safe) sessionStorage.removeItem('portal_return_to');
    return <Navigate to={safe ?? '/'} replace />;
  }

  if (autoEmail) {
    return <SignupProgress stage="signing_in" />;
  }

  const portalTitle = branding.portal_title || t('login.title');
  const loginBgColor = branding.login_background_color || '#fafafa';
  const hasLogo = !!(branding.logo_url && branding.logo_url.trim());
  const isBranded = !!(branding.portal_title || branding.primary_color);

  const stepAnimation = `${stepFadeIn} 0.3s ease-out both`;

  return (
    <Box
      sx={{
        'minHeight': '100vh',
        'display': 'flex',
        'flexDirection': 'column',
        'justifyContent': 'center',
        'alignItems': 'center',
        'bgcolor': loginBgColor,
        'position': 'relative',
        'overflow': 'hidden',
      }}
    >
      <Box
        sx={(theme) => ({
          'position': 'absolute',
          'inset': 0,
          'backgroundImage': `
            radial-gradient(circle at 20% 30%, ${theme.palette.primary.main}15 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, ${theme.palette.secondary.main}12 0%, transparent 50%)
          `,
        })}
      />

      <Box
        sx={{
          'position': 'relative',
          'zIndex': 1,
          'display': 'flex',
          'flexDirection': 'column',
          'alignItems': 'stretch',
          'maxWidth': 400,
          'width': '100%',
          'px': 3,
        }}
      >
        <Box sx={{ mb: 3, ml: 0.5, animation: `${logoFadeIn} 0.5s ease-out both` }}>
          {hasLogo ? (
            <Box
              component="img"
              src={branding.logo_url!}
              alt="logo"
              sx={{ maxHeight: '3rem', maxWidth: 220, width: 'auto' }}
            />
          ) : !isBranded ? (
            <XxllncAiLogo height={40} />
          ) : null}
        </Box>

        <Box
          sx={{
            'width': '100%',
            'boxSizing': 'border-box',
            'bgcolor': 'background.paper',
            'borderRadius': '20px',
            'border': '1px solid rgba(0,0,0,0.07)',
            'boxShadow': '0 1px 2px rgba(0,0,0,0.03), 0 12px 32px rgba(0,0,0,0.05)',
            'p': 4.5,
            'textAlign': 'left',
            'animation': `${cardSlideUp} 0.5s ease-out 0.15s both`,
          }}
        >
          <Typography
            sx={{
              'fontSize': '1.6rem',
              'fontWeight': 700,
              'letterSpacing': '-0.02em',
              'color': 'text.primary',
              'mb': 0.75,
            }}
          >
            {portalTitle}
          </Typography>

          <Typography
            sx={{
              'fontSize': '0.95rem',
              'color': 'text.secondary',
              'mb': 4,
              'lineHeight': 1.55,
            }}
          >
            {t('login.defaultSubtitle')}
          </Typography>

          {step === 'email' && sessionExpired && (
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              {t('login.sessionExpired')}
            </Alert>
          )}

          {step === 'email' && (
            <Box sx={{ animation: stepAnimation }}>
              <Box component="form" onSubmit={handleSubmit}>
                <Typography
                  component="label"
                  htmlFor="login-email"
                  sx={{
                    display: 'block',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: 'text.primary',
                    mb: 1,
                  }}
                >
                  {t('login.email')}
                </Typography>
                <TextField
                  id="login-email"
                  fullWidth
                  hiddenLabel
                  placeholder={t('login.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={!!email && !isValidEmail(email)}
                  helperText={!!email && !isValidEmail(email) ? t('login.notValid') : undefined}
                  autoFocus
                  sx={{ 'mb': 2.5, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  type="submit"
                  endIcon={<KeyboardDoubleArrowRightIcon />}
                  sx={{
                    'py': 1.5,
                    'px': 2.75,
                    'fontSize': '0.95rem',
                    'fontWeight': 600,
                    'borderRadius': '999px',
                    'textTransform': 'none',
                    'boxShadow': 'none',
                    'justifyContent': 'space-between',
                    'bgcolor': 'primary.main',
                    '& .MuiButton-endIcon': { ml: 0 },
                    '&:hover': { bgcolor: 'primary.dark', boxShadow: 'none' },
                  }}
                >
                  {t('login.continue')}
                </Button>
              </Box>

              <Box
                sx={{
                  'mt': 3,
                  'p': 2,
                  'borderRadius': '14px',
                  'bgcolor': 'rgba(0,0,0,0.035)',
                  'opacity': 0,
                  'animation': `${stepFadeIn} 0.4s ease-out 0.12s forwards`,
                }}
              >
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 1.25, pl: 0.25 }}>
                  {t('login.or')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <BrandButton
                    glyph={<GoogleGlyph />}
                    label={t('login.signInGoogle')}
                    onClick={() => handleDirectBrokerLogin('google')}
                  />
                  <BrandButton
                    glyph={<MicrosoftGlyph />}
                    label={t('login.signInMicrosoft')}
                    onClick={() => handleDirectBrokerLogin('microsoft-entra')}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {step === 'loading' && (
            <Box sx={{ py: 3, animation: stepAnimation }}>
              <CircularProgress size={28} thickness={4} />
              <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: '0.9rem' }}>
                {t('login.loading')}
              </Typography>
            </Box>
          )}

          {step === 'register' && (
            <Box sx={{ animation: stepAnimation }}>
              <Typography
                sx={{
                  'fontSize': '1.05rem',
                  'fontWeight': 600,
                  'letterSpacing': '-0.01em',
                  'color': 'text.primary',
                  'mb': 0.75,
                }}
              >
                {t('login.registerTitle')}
              </Typography>
              <Typography
                sx={{
                  'fontSize': '0.85rem',
                  'color': 'text.secondary',
                  'mb': 3,
                  'lineHeight': 1.6,
                }}
              >
                {t('login.registerSubtitle')}
              </Typography>
              <TextField
                fullWidth
                label={t('login.email')}
                value={email}
                disabled
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label={t('login.organizationName')}
                placeholder={t('login.organizationNamePlaceholder')}
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                autoFocus
                sx={{ mb: 3 }}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 2,
                  color: 'text.disabled',
                  fontSize: '0.75rem',
                  '&::before, &::after': {
                    content: '""',
                    flex: 1,
                    height: '1px',
                    bgcolor: 'rgba(0,0,0,0.08)',
                  },
                }}
              >
                <Box component="span" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {t('login.continueWith')}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2 }}>
                <BrandButton
                  glyph={<GoogleGlyph />}
                  label={t('login.signUpGoogle')}
                  disabled={!orgName.trim()}
                  onClick={() => handleSignupWithBroker('google')}
                />
                <BrandButton
                  glyph={<MicrosoftGlyph />}
                  label={t('login.signUpMicrosoft')}
                  disabled={!orgName.trim()}
                  onClick={() => handleSignupWithBroker('microsoft-entra')}
                />
                <BrandButton
                  glyph={<EmailGlyph />}
                  label={t('login.signUpEmail')}
                  disabled={!orgName.trim()}
                  onClick={handleSignupWithEmail}
                />
              </Box>
              <Button variant="text" fullWidth onClick={handleBack} sx={{ textTransform: 'none' }}>
                {t('login.back')}
              </Button>
            </Box>
          )}

          {step === 'error' && (
            <Box sx={{ animation: stepAnimation }}>
              <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                {errorDetail || t('errors.tryAgain')}
              </Alert>
              <Button
                variant="text"
                fullWidth
                onClick={handleBack}
                sx={{ textTransform: 'none' }}
              >
                {t('login.back')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
